import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase";

export default function Schedule() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    game_date: "",
    game_time: "17:00",
    opponent: "",
    location: "",
    is_home: false,
    result: "",
    notes: "",
  });

  const fetchGames = useCallback(async () => {
    const { data } = await supabase
      .from("schedule")
      .select("*")
      .order("game_date")
      .order("game_time");
    if (data) setGames(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  const openAdd = () => {
    setEditingGame(null);
    setForm({
      game_date: "",
      game_time: "17:00",
      opponent: "",
      location: "",
      is_home: false,
      result: "",
      notes: "",
    });
    setShowForm(true);
  };

  const openEdit = (game) => {
    setEditingGame(game);
    setForm({
      game_date: game.game_date,
      game_time: game.game_time?.slice(0, 5) || "17:00",
      opponent: game.opponent,
      location: game.location,
      is_home: game.is_home,
      result: game.result || "",
      notes: game.notes || "",
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      game_date: form.game_date,
      game_time: form.game_time,
      opponent: form.opponent,
      location: form.location,
      is_home: form.is_home,
      result: form.result || null,
      notes: form.notes || null,
    };

    if (editingGame) {
      await supabase.from("schedule").update(payload).eq("id", editingGame.id);
    } else {
      await supabase.from("schedule").insert(payload);
    }

    setSaving(false);
    setShowForm(false);
    fetchGames();
  };

  const deleteGame = async (id) => {
    await supabase.from("schedule").delete().eq("id", id);
    fetchGames();
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const display = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${display}:${m} ${ampm}`;
  };

  const today = new Date().toISOString().split("T")[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-navy/40">Loading schedule...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-navy">Schedule</h1>
        <button
          onClick={openAdd}
          className="bg-navy text-white text-sm font-semibold py-2.5 px-5 rounded-lg hover:bg-navy-light transition-colors"
        >
          + Add Game
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-warm-gray p-5 mb-6">
          <h2 className="font-semibold text-navy mb-4">
            {editingGame ? "Edit Game" : "Add New Game"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">Date</label>
                <input
                  type="date"
                  required
                  value={form.game_date}
                  onChange={(e) => setForm({ ...form, game_date: e.target.value })}
                  className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">Time</label>
                <input
                  type="time"
                  required
                  value={form.game_time}
                  onChange={(e) => setForm({ ...form, game_time: e.target.value })}
                  className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">Opponent</label>
                <input
                  type="text"
                  required
                  value={form.opponent}
                  onChange={(e) => setForm({ ...form, opponent: e.target.value })}
                  placeholder="vs Team Name"
                  className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">Location</label>
                <input
                  type="text"
                  required
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Field name / address"
                  className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">Result</label>
                <input
                  type="text"
                  value={form.result}
                  onChange={(e) => setForm({ ...form, result: e.target.value })}
                  placeholder="e.g. W 5-3, L 2-4 (optional)"
                  className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_home}
                  onChange={(e) => setForm({ ...form, is_home: e.target.checked })}
                  className="w-4 h-4 rounded border-warm-gray text-navy focus:ring-navy"
                />
                <span className="text-sm text-navy">Home Game</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">
                Notes <span className="font-normal text-navy/40">(optional)</span>
              </label>
              <input
                type="text"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. Senior Night, Doubleheader"
                className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="bg-black hover:bg-neutral-800 text-white font-semibold text-sm py-2.5 px-6 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? "Saving..." : editingGame ? "Update Game" : "Add Game"}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="border border-warm-gray text-navy/60 text-sm font-medium py-2.5 px-6 rounded-lg hover:bg-warm-gray transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Games list */}
      <div className="bg-white rounded-xl border border-warm-gray overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-warm-gray bg-cream/50">
                <th className="text-left px-5 py-2.5 font-semibold text-navy/60">Date</th>
                <th className="text-left px-5 py-2.5 font-semibold text-navy/60">Time</th>
                <th className="text-left px-5 py-2.5 font-semibold text-navy/60">Opponent</th>
                <th className="text-left px-5 py-2.5 font-semibold text-navy/60">Location</th>
                <th className="text-center px-5 py-2.5 font-semibold text-navy/60">H/A</th>
                <th className="text-center px-5 py-2.5 font-semibold text-navy/60">Result</th>
                <th className="text-right px-5 py-2.5 font-semibold text-navy/60">Actions</th>
              </tr>
            </thead>
            <tbody>
              {games.map((g) => {
                const isPast = g.game_date < today;
                return (
                  <tr
                    key={g.id}
                    className={`border-b border-warm-gray last:border-0 hover:bg-cream/30 ${
                      isPast && !g.result ? "opacity-50" : ""
                    }`}
                  >
                    <td className="px-5 py-3 font-medium text-navy whitespace-nowrap">
                      {formatDate(g.game_date)}
                    </td>
                    <td className="px-5 py-3 text-navy/60 whitespace-nowrap">
                      {formatTime(g.game_time)}
                    </td>
                    <td className="px-5 py-3 text-navy">
                      <span className="font-medium">{g.opponent}</span>
                      {g.notes && (
                        <span className="ml-2 text-xs text-navy/40">({g.notes})</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-navy/60 max-w-[200px] truncate">
                      {g.location}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          g.is_home
                            ? "bg-green-100 text-green-700"
                            : "bg-warm-gray text-navy/50"
                        }`}
                      >
                        {g.is_home ? "Home" : "Away"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-center">
                      {g.result ? (
                        <span
                          className={`text-xs font-bold ${
                            g.result.startsWith("W")
                              ? "text-green-600"
                              : g.result.startsWith("L")
                              ? "text-red-500"
                              : "text-navy/60"
                          }`}
                        >
                          {g.result}
                        </span>
                      ) : (
                        <span className="text-xs text-navy/30">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(g)}
                        className="text-navy/50 hover:text-navy text-xs font-medium mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteGame(g.id)}
                        className="text-red-500 hover:text-red-700 text-xs font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
              {games.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-navy/40">
                    No games scheduled yet. Click "Add Game" to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
